import { useQuery } from '@tanstack/react-query'
import { Box, Server, HardDrive, Activity, Cpu, MemoryStick } from 'lucide-react'
import Card, { CardHeader } from '../components/Card'
import { getSystemStats, getContainers, getVMs } from '../lib/api'
import type { Container, VM } from '../types'
import StatusBadge from '../components/StatusBadge'
import { Link } from 'react-router-dom'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

export default function Dashboard() {
  const { data: stats, isLoading: _statsLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: getSystemStats,
    refetchInterval: 10000,
  })

  const { data: containers = [] } = useQuery({
    queryKey: ['containers'],
    queryFn: getContainers,
    refetchInterval: 5000,
  })

  const { data: vms = [] } = useQuery({
    queryKey: ['vms'],
    queryFn: getVMs,
    refetchInterval: 5000,
  })

  const runningContainers = containers.filter((c: Container) => c.state === 'running')
  const runningVMs = vms.filter((v: VM) => v.state === 'running')

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Voltainer Dashboard</h1>
        <p className="mt-1 text-gray-500">Linux container &amp; VM runtime &mdash; part of the Volt Platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Containers"
          value={containers.length}
          subtitle={`${runningContainers.length} running`}
          icon={<Box className="h-5 w-5 text-voltainer-600" />}
          color="voltainer"
        />
        <StatCard
          title="VMs"
          value={vms.length}
          subtitle={`${runningVMs.length} running`}
          icon={<Server className="h-5 w-5 text-armored-600" />}
          color="armored"
        />
        <StatCard
          title="Storage"
          value={stats ? formatBytes(stats.storage.total_size) : '-'}
          subtitle={`${stats?.storage.total_blobs || 0} blobs`}
          icon={<HardDrive className="h-5 w-5 text-blue-600" />}
          color="blue"
        />
        <StatCard
          title="Uptime"
          value={stats ? formatUptime(stats.uptime_seconds) : '-'}
          subtitle="System uptime"
          icon={<Activity className="h-5 w-5 text-green-600" />}
          color="green"
        />
      </div>

      {/* Resource Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader title="CPU Usage" />
          <div className="flex items-center space-x-4">
            <Cpu className="h-10 w-10 text-voltainer-500" />
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">Usage</span>
                <span className="font-medium">
                  {stats?.system_resources.cpu_percent.toFixed(1) || 0}%
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-voltainer-400 to-voltainer-600 rounded-full transition-all"
                  style={{ width: `${stats?.system_resources.cpu_percent || 0}%` }}
                />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Memory Usage" />
          <div className="flex items-center space-x-4">
            <MemoryStick className="h-10 w-10 text-armored-500" />
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-500">
                  {formatBytes(stats?.system_resources.memory_bytes || 0)} used
                </span>
                <span className="font-medium">
                  {stats?.system_resources.memory_percent.toFixed(1) || 0}%
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-armored-400 to-armored-600 rounded-full transition-all"
                  style={{ width: `${stats?.system_resources.memory_percent || 0}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader
            title="Running Containers"
            action={
              <Link to="/containers" className="text-sm text-voltainer-600 hover:text-voltainer-700">
                View all →
              </Link>
            }
          />
          {runningContainers.length === 0 ? (
            <p className="text-gray-500 text-sm">No running containers</p>
          ) : (
            <div className="space-y-3">
              {runningContainers.slice(0, 5).map((container: Container) => (
                <Link
                  key={container.id}
                  to={`/containers/${encodeURIComponent(container.id)}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Box className="h-5 w-5 text-gray-400" />
                    <span className="font-medium text-gray-900">{container.id}</span>
                  </div>
                  <StatusBadge status={container.state} />
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Running VMs"
            action={
              <Link to="/vms" className="text-sm text-armored-600 hover:text-armored-700">
                View all →
              </Link>
            }
          />
          {runningVMs.length === 0 ? (
            <p className="text-gray-500 text-sm">No running VMs</p>
          ) : (
            <div className="space-y-3">
              {runningVMs.slice(0, 5).map((vm: VM) => (
                <Link
                  key={vm.id}
                  to={`/vms/${encodeURIComponent(vm.id)}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Server className="h-5 w-5 text-gray-400" />
                    <div>
                      <span className="font-medium text-gray-900">{vm.id}</span>
                      <span className="text-xs text-gray-500 ml-2">{vm.memory}</span>
                    </div>
                  </div>
                  <StatusBadge status={vm.state} />
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string | number
  subtitle: string
  icon: React.ReactNode
  color: 'voltainer' | 'armored' | 'blue' | 'green'
}

function StatCard({ title, value, subtitle, icon, color }: StatCardProps) {
  const bgColors = {
    voltainer: 'bg-voltainer-50',
    armored: 'bg-armored-50',
    blue: 'bg-blue-50',
    green: 'bg-green-50',
  }

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className={`p-2 rounded-lg ${bgColors[color]}`}>{icon}</div>
      </div>
    </Card>
  )
}
